import { FC, useCallback, useMemo } from 'react'
import { StreamControlPreset, CONTROL_OFF, CONTROL_ON, ControlState } from 'components/features/coaching/advisorVideoCall/StreamControlPreset/StreamControlPreset'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { DeviceSelect } from 'components/features/coaching/advisorVideoCall/DeviceSelect/DeviceSelect'
import { AgoraMuteVideoAction, AgoraUnmuteVideoAction, AgoraVideoInputAction } from 'components/features/videoCalls/types/actions'
import { agoraInstances } from 'components/features/videoCalls/agoraInstances'
import { useDevices } from 'components/features/videoCalls/useDevices'


type CamControlProps = {
  isFullScreen: boolean
}
export const CamControl: FC<CamControlProps> = (props) => {
  const { isFullScreen } = props
  const muteState = useStoreSelector((state) => state.app.videoCall.localStream.videoMuteState)
  const clientState = useStoreSelector((state) => state.app.videoCall.client.state)
  const localStreamState = useStoreSelector((state) => state.app.videoCall.localStream.state)

  const storeDeviceId = useStoreSelector((state) => state.app.videoCall.localStream.videoInputDeviceId)
  const localStreamId = useStoreSelector((state) => state.app.videoCall.localStream.streamId)
  const stream = agoraInstances.streams.get(localStreamId as any)

  const devices = useDevices(stream, 'videoinput')
  const permissions = useStoreSelector((state) => state.app.videoCall.permissions.cam)


  const dispatch = useStoreDispatch()

  const handleDeviceChange = useCallback((value: string | null) => {
    if (value === null) {
      return
    }
    dispatch(new AgoraVideoInputAction(value))
  }, [dispatch])

  const handleClick = useCallback(() => {
    if (muteState === 'mute') {
      dispatch(new AgoraUnmuteVideoAction(localStreamId))
    } else if (muteState === 'unmute') {
      dispatch(new AgoraMuteVideoAction(localStreamId))
    }
  }, [muteState, dispatch, localStreamId])

  const devicesSelectChild = useMemo(() => {
    if (!devices || devices.length === 0) {
      return null
    }
    const options = devices.map(({ label, deviceId }, index) => ({
      label: label || `Device ${ index }`,
      value: deviceId,
    }))

    return (
      <DeviceSelect
        options={ options }
        value={ storeDeviceId }
        onChange={ handleDeviceChange }
        placeholder='Select Camera'
      />
    )
  }, [devices, storeDeviceId, handleDeviceChange])

  const contentChild = useMemo(() => {
    if (permissions === 'denied') {
      return <span><strong>Camera</strong> <br /><small>Check browser permissions</small></span>
    }

    if (devicesSelectChild) {
      return devicesSelectChild
    }

    return <span onClick={ handleClick }>Camera</span>
  }, [permissions, devicesSelectChild, handleClick])

  const attrs = useMemo(() => {
    const nextAttrs = {
      state: CONTROL_OFF as ControlState,
      status: 'Off',
      isProgress: false,
      isDisabled: false,
    }

    if (clientState === 'progress') {
      nextAttrs.isDisabled = true
    }


    if (muteState === 'muting' || muteState === 'unmuting') {
      nextAttrs.isProgress = true
      nextAttrs.isDisabled = true
    }

    if (muteState === 'unmute' || muteState === 'unmuting') {
      nextAttrs.state = CONTROL_ON
      nextAttrs.status = 'On'
    }

    if (localStreamState === 'progress') {
      nextAttrs.isDisabled = true
      nextAttrs.isProgress = true
    }

    if (permissions === 'denied') {
      nextAttrs.status = 'Camera unavailable'
      nextAttrs.isDisabled = true
      nextAttrs.state = CONTROL_OFF
      nextAttrs.status = 'Off'
    }

    return nextAttrs
  }, [muteState, permissions, clientState, localStreamState])

  return (
    <StreamControlPreset
      { ...attrs }
      isFullScreen={ isFullScreen }
      offIconSlug='video-off'
      onIconSlug='video'
      onClick={ handleClick }
    >
      { contentChild }
    </StreamControlPreset>
  )
}


